/**
 * "…when?" — the matches spread across time.
 *
 * The chart is `MiniBars` from the dashboard rather than a second bar renderer, so a busy hour
 * looks the same here as it does on the summary cards. What this file owns is the translation:
 * the timeline aggregate returns only the buckets that had rows, and a chart has to be
 * contiguous.
 *
 * ─── ZERO-FILLING, AND WHERE THE AXIS STARTS ───
 * Missing buckets are filled with zeros, because a chart that closes its own gaps turns a quiet
 * afternoon into a continuous run of activity. But the span runs from the FIRST to the LAST
 * bucket that had something in it, not across the whole query window: "today" at 10am would
 * otherwise be fourteen empty hours with two bars at the end, and the shape of the day — which is
 * the only reason to draw this at all — would be squeezed into a sixth of the width. The axis
 * states both ends, so the narrowing is visible rather than implied.
 */
import type { SearchResult } from '../../../api/contracts';
import { AxisRow, MiniBars, type Series } from '../../dashboard/MiniCharts';
import { AnswerEmpty, AnswerShell, Caveat, Figure, fmtDateTime } from './common';

interface TimelineRow {
  bucket: string;
  events: number;
  distinct_persons?: number;
}

/** The buckets that had rows → a contiguous series, zero-filled, first hit to last. */
function toSeries(rows: TimelineRow[], bucket: 'hour' | 'day'): { series: Series; peakIndex: number } | null {
  if (!rows.length) return null;
  const step = bucket === 'day' ? 86400 : 3600;

  const byBucket = new Map<number, number>();
  for (const r of rows) {
    const t = Math.floor(new Date(r.bucket).getTime() / 1000);
    if (!Number.isFinite(t)) continue;
    byBucket.set(t, (byBucket.get(t) || 0) + r.events);
  }
  const stamps = [...byBucket.keys()].sort((a, b) => a - b);
  if (!stamps.length) return null;

  const start = stamps[0];
  const end = stamps[stamps.length - 1];
  const values: number[] = [];
  for (let t = start; t <= end; t += step) values.push(byBucket.get(t) || 0);

  return { series: { start, step, values }, peakIndex: values.indexOf(Math.max(...values)) };
}

export default function WhenAnswer({ result }: { result: SearchResult }) {
  const rows = (result.rows || []) as TimelineRow[];
  const bucket = (result.bucket || 'hour') as 'hour' | 'day';
  const built = toSeries(rows, bucket);

  if (!built) return <AnswerEmpty cameraScope={result.camera_scope} />;

  const { series, peakIndex } = built;
  const total = result.events ?? series.values.reduce((a, b) => a + b, 0);
  const distinct = result.distinct_persons;

  const at = (i: number) => new Date((series.start + i * series.step) * 1000);
  const first = at(0);
  const last = at(series.values.length - 1);
  const peakAt = at(peakIndex);

  const label = (d: Date) => (bucket === 'day'
    ? d.toLocaleDateString([], { month: 'short', day: 'numeric' })
    : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }));

  /** The busiest bucket, named as a span — an "hour" bar covers 14:00 to 15:00, not an instant. */
  const peakSpan = bucket === 'day'
    ? label(peakAt)
    : `${label(peakAt)}–${label(new Date(peakAt.getTime() + 3600_000))}`;

  return (
    <AnswerShell
      headline={`Busiest ${bucket === 'day' ? 'day' : 'hour'}: ${peakSpan}`}
      sub={`First match ${fmtDateTime(first.toISOString())} · last match ${fmtDateTime(last.toISOString())}`}
      figures={(
        <>
          <Figure value={total} label={total === 1 ? 'detection' : 'detections'} />
          {typeof distinct === 'number' && (
            <Figure value={distinct} label={distinct === 1 ? 'distinct person' : 'distinct people'} />
          )}
          <Figure value={series.values[peakIndex]} label={`in the busiest ${bucket}`} />
          {typeof result.camera_scope === 'number' && (
            <Figure value={result.camera_scope} label={result.camera_scope === 1 ? 'camera searched' : 'cameras searched'} />
          )}
        </>
      )}
      caveats={(
        <>
          <Caveat>
            The chart spans the first match to the last, not the whole window — so a quiet morning
            is not drawn. Empty {bucket}s inside that span ARE drawn, as zero.
          </Caveat>
          {result.distinct_omitted && (
            <Caveat>
              Counting distinct people over this many events takes seconds, so it was skipped.
              Narrow the window or add a filter and the figure returns.
            </Caveat>
          )}
        </>
      )}
    >
      <div className="rounded-md border border-border bg-card p-3">
        <MiniBars series={series} color="var(--brand)" emphasis="peak" height={128} unit=" detections" />
        <AxisRow
          left={label(first)}
          center={`${series.values.length} ${bucket === 'day' ? 'days' : 'hours'} · peak ${series.values[peakIndex]}`}
          right={label(last)}
        />
      </div>
    </AnswerShell>
  );
}
